import React, { useState, useEffect } from 'react';
import {
  Pharmacy,
  GetPharmaciesByLocationAPIResponse,
} from '@avicennapharmacy/managemymeds-shared';
import { components, IndicatorProps } from 'react-select';
import ReactAsyncSelect from 'react-select/async';
import Axios from 'axios';
import { isValid } from 'postcode';
import {
  Option, multiLineString, SEARCH_DEBOUNCE_WAIT,
} from './shared';
import { IconChevronDown } from './icons';
import styled from 'styled-components';
import { customStyles } from '../theme/customSelectStyles';

/**
 * This component relies on react-select which throws a deprecated warning as an error.
 * The bug is being addressed by the authors: https://github.com/JedWatson/react-select/issues/3703
 */

type PharmacySearchProps = {
  loading: boolean;
  select: (pharmacy: Pharmacy) => void;
  allPharmacies: Pharmacy[];
  owned: string | string[] | null;
};

const StyledIconChevronDown = styled(IconChevronDown)`
  margin-top: 1px;
  width: 17px;
  height: 17px;
`;

const DropdownIndicator = (props: IndicatorProps<any, any>) => (
  <components.DropdownIndicator {...props}>
    <StyledIconChevronDown />
  </components.DropdownIndicator>
);

const AsyncSelect = ({ label, id, ...props }: any) => (
  <FullWidthWrapper>
    {label && <Label htmlFor={id}>{label}</Label>}
    <StyledAsyncSelect
      id={id}
      aria-label={label}
      styles={customStyles}
      components={{ DropdownIndicator }}
      cacheOptions
      {...props}
    />
  </FullWidthWrapper>
);

const PharmacySearch = ({
  loading, select, allPharmacies, owned
}: PharmacySearchProps) => {
  const [defaultOptions, setDefaultOptions] = useState<Option<Pharmacy>[]>([]);
  const [,setError] = useState('');

  const generateOptions = (options: Pharmacy[]) => {
    return options.map((pharmacy: Pharmacy): Option<Pharmacy> => {
      const singleLine = multiLineString([
        pharmacy.businessName,
        pharmacy.address1,
        pharmacy.address2,
        pharmacy.address3,
        pharmacy.city,
        pharmacy.postcode,
      ]);
      return {
        label: singleLine,
        value: { ...pharmacy, id: pharmacy.id },
      };
    });
  }

  const loadOptions = async (search: string) => {
    if (search) {
      if (isValid(search)) {
        try {
          setError('');
          const { data } = await Axios.post<GetPharmaciesByLocationAPIResponse>(
            process.env.GATSBY_PHARMACY_SEARCH_ENDPOINT!, {
              postcode: search,
              tag: '',
              distance: 250,
            },
          );
          return generateOptions(data.pharmacies.filter((p: any) => {
            return (
              p.isServiceBookingEnabled === true &&
              (owned === "true" ? p.ownershipType === "Owned" : true)
            );
          }));
        } catch (error) {
          setError('Unable to search for pharmacies at this time. Please try again.');
          return defaultOptions;
        }
      }
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(generateOptions(
            allPharmacies.filter(
              (pharmacy: Pharmacy) => {
                return pharmacy.postcode.toLowerCase().includes(search.toLowerCase())
                        || pharmacy.businessName.toLowerCase().includes(search.toLowerCase())
                        || pharmacy.city.toLowerCase().includes(search.toLowerCase())
                        || pharmacy.address1.toLowerCase().includes(search.toLowerCase())
                        || pharmacy.address2.toLowerCase().includes(search.toLowerCase())
                        || pharmacy.address3.toLowerCase().includes(search.toLowerCase());
              },
            ),
          ));
        }, SEARCH_DEBOUNCE_WAIT);
      });
    }
    return defaultOptions;
  };

  useEffect(() => {
    if (allPharmacies) {
      setDefaultOptions(generateOptions(allPharmacies));
    }
  }, [allPharmacies]);

  return (
    <AsyncSelect
      aria-label="Find pharmacy"
      placeholder={loading ? "Loading your location..." : "Search nearby postcode or pharmacy name"}
      defaultOptions={defaultOptions}
      loadOptions={loadOptions}
      onChange={(option: Option<Pharmacy>) => select(option.value)}
      disabled={loading}
      classNamePrefix="select-pharmacy"
    />
  );
};

export default PharmacySearch;

const FullWidthWrapper = styled.div`
  width: 100%;
`;

type FormElementProps = {
  label?: string;
  wrap?: boolean;
  noMargin?: boolean;
};

const StyledAsyncSelect = styled(ReactAsyncSelect)<FormElementProps>`
  ${(props) => !props.noMargin && 'margin-bottom: 12px'};
`;

export const Label = styled.label`
  color: ${(props) => props.theme.colors.primary};
  font-size: 16px;
  line-height: 24px;
  margin-bottom: 2px;
  display: inline-block;
  flex-grow: 100;
  margin-right: 10px;
`;